<template>
    <div
        data-test-role="input-file-wrapper"
        no-interact>
        <div v-if="label">
            <pc-label
                v-if="label"
                v-bind="labelBinder"
                v-bind:for="internalId"
                v-on:help-clicked="$emit('help-clicked')" />
        </div>
        <div
            v-if="!hasFile && !uploading"
            data-test-role="input-file">
            <label
                tabindex="0"
                class="form-control pc-custom-file"
                v-bind:readonly="readonly"
                v-bind:data-browse="browseButtonLabel"
                v-on:keydown.space.prevent
                v-on:keyup="keyupHandler"
                v-on:dragover.prevent
                v-on:drop.capture="dropHandler">
                {{ placeholder }}
                <input
                    v-bind="binder"
                    ref="input"
                    v-bind:accept="accept"
                    v-bind:disabled="disabled || readonly"
                    data-test-role="new-file-uploader"
                    lang="en"
                    class="invisible"
                    style="height: 1rem"
                    v-on:change="onFileChanged" />
            </label>
            <div
                v-if="showExtensions"
                class="small">
                {{ allowedFileTypes }}{{ accept }}
            </div>
        </div>
        <div
            v-if="uploading"
            data-test-role="input-file-uploading"
            class="input-group w-100 flex-nowrap">
            <pc-loader
                v-if="uploading"
                v-bind:loading="uploading"
                class="input-group-text"
                small
                no-bg>
                <span class="pc-file-width-spacer" />
            </pc-loader>
            <div class="flex-grow-1 bot-loading-bar">
                <input
                    v-bind:placeholder="uploadMessage"
                    type="text"
                    disabled
                    class="form-control rounded-0 bg-white" />
            </div>
            <button
                class="btn border btn-secondary input-group-text"
                type="button"
                data-test-role="cancel-btn"
                v-on:click="cancelUpload">
                {{ cancelButton }}
            </button>
        </div>
        <div
            v-if="hasFile"
            data-test-role="input-file-uploaded"
            v-bind:class="hasFileClasses.wrapper">
            <input
                v-bind:id="internalId"
                ref="input"
                v-bind:value="modelValue.FileName"
                disabled
                type="text"
                v-bind:class="{ 'bg-white': !(disabled || readonly) }"
                class="form-control" />
            <button
                v-if="showView"
                class="btn border btn-secondary input-group-text"
                type="button"
                data-test-role="view-btn"
                v-on:click="viewFile">
                {{ viewButton }}
            </button>
            <button
                v-if="showRemove"
                class="btn border btn-secondary input-group-text"
                type="button"
                data-test-role="remove-btn"
                v-bind:disabled="disabled || readonly"
                v-on:click="removeFile">
                {{ removeButton }}
            </button>
        </div>
        <div
            v-if="showValidationErrors"
            data-test-role="pc-file-val-error"
            v-bind:class="{ 'd-block': hasValidationError }"
            class="invalid-feedback fw-bold">
            {{ validationError }}
        </div>
        <pc-alert
            v-model:shown="showAlert"
            v-bind:message="alertMessage"
            v-bind:severity="alertSeverity"
            v-bind:duration="4000"
            data-test-role="input-file-alert"
            class="mt-2" />
    </div>
</template>

<script>
    import base from "./_inputBase.vue";
    import { downloadFile, DOCUMENT_WRAPPER_TYPE } from "Util/util";
    import { ALERT_SEVERITY } from "Util/alerts";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/pcFile.i18n";
    import { BP } from "Responsive/breakpoint";
    import { isAtLeast } from "Responsive/manager";

    const MAX_ALLOWED_FILENAME_LENGTH = 128;

    export default {
        extends: base,
        inject: {
            viewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            modelValue: { type: Object, required: false, default: null },
            isTemporary: { type: Boolean, required: false, default: true },
            extensions: { type: Array, required: false, default: () => ["pdf", "docx", "xlsx", "csv"] },
            showView: { type: Boolean, required: false, default: true },
            showRemove: { type: Boolean, required: false, default: true },
            showExtensions: { type: Boolean, required: false, default: false },
            customRemoveFileMessage: { type: String, required: false, default: null },
            // Suppress the confirmation dialog when removing an uploaded file.
            suppressRemoveConfirmation: { type: Boolean, required: false, default: false },
            // Note that the web server may also impose a size limit on
            // uploaded files depending on the actual upload method
            maxAllowedSizeKb: { type: Number, required: false, default: 5 * 1024 },
            uploadFunction: { type: Function, required: false, default: null },
            viewFunction: { type: Function, required: false, default: null }
        },
        emits: ["help-clicked", "uploading-changed", "update:modelValue", "upload-canceled"],
        data: function () {
            return {
                uploadMessage: "Uploading...",
                internalType: "file",
                uploading: false,
                file: null,
                cancelFlag: false,
                alertSeverity: ALERT_SEVERITY.error,
                alertMessage: null,
                showAlert: false
            };
        },
        computed: {
            hasFile() {
                return Boolean(this.modelValue);
            },

            accept() {
                if (this.extensions && this.extensions.length > 0) {
                    return this.extensions.map((e) => `.${e}`).join(", ");
                }
                return null;
            },
            hasFileClasses() {
                const atLeastSm = isAtLeast(this.viewport, BP.sm);

                return {
                    wrapper: atLeastSm ? "input-group" : null
                };
            },
            removeDialogMessage() {
                return this.customRemoveFileMessage || this.removeDialogMessageDefault;
            },
            ...mapI18n(
                {
                    ...KEYS,
                    uploadingMessage() {
                        return {
                            key: KEYS.uploadingMessageText,
                            values: {
                                filename: this.file.name
                            }
                        };
                    },
                    singleExtensionInvalidMessage() {
                        return {
                            key: KEYS.singleExtensionInvalidMessageText,
                            values: {
                                extension: this.extensions[0]
                            }
                        };
                    },
                    fileTooLargeMessage() {
                        return {
                            key: KEYS.fileTooLargeMessageText,
                            values: {
                                sizeMb: (this.maxAllowedSizeKb / 1024).toFixed(2)
                            }
                        };
                    }
                },
                NAMESPACE
            )
        },
        watch: {
            uploading(value) {
                this.$emit("uploading-changed", value);
            }
        },
        methods: {
            clearFile() {
                this.$emit("update:modelValue", null);
            },
            cancelUpload() {
                this.$emit("upload-canceled");
                this.cancelFlag = true;
                if (this.xhr) {
                    this.xhr.abort();
                }
                this.clearFile();
            },
            async removeFile() {
                if (this.suppressRemoveConfirmation) {
                    this.clearFile();
                    return;
                }

                const cancelResult = "CANCEL";
                const okResult = "OK";
                const result = await this.$app.showDialog({
                    title: this.removeDialogTitle,
                    message: this.removeDialogMessage,
                    buttons: [
                        {
                            text: this.cancelRemoveButton,
                            result: cancelResult
                        },
                        {
                            text: this.approveRemoveButton,
                            primary: true,
                            result: okResult,
                            testRole: "attachment-remove-ok"
                        }
                    ],
                    testRole: "attachment-remove-dialog"
                });
                if (result === okResult) {
                    this.clearFile();
                }
            },
            viewFile() {
                if (!this.hasFile) {
                    return;
                }
                if (this.viewFunction) {
                    this.viewFunction();
                    return;
                }
                const url =
                    this.$app.DownloadDocumentUrl +
                    `?ProgramId=${this.$app.ProgramId}` +
                    `&${DOCUMENT_WRAPPER_TYPE.DocumentId}=${this.modelValue.DocumentId}`;
                downloadFile(url);
            },
            async onFileChanged(event) {
                try {
                    this.uploading = true;
                    this.cancelFlag = false;
                    const file = this.grabAndValidateFile(event);
                    if (file) {
                        this.file = file;
                        this.uploadMessage = this.uploadingMessage;
                        const result = await this.uploadFile({ file, isTemporary: this.isTemporary });
                        if (!this.cancelFlag && result) {
                            this.$emit("update:modelValue", result);
                        }
                    }
                } finally {
                    this.uploading = false;
                }
            },
            uploadFile({ file, isTemporary }) {
                if (this.readonly) {
                    return;
                }

                if (this.uploadFunction) {
                    return this.uploadFunction(file);
                } else {
                    return this.$app.uploadFile({ file, isTemporary, captureXhr: this.captureXhr });
                }
            },
            captureXhr(xhr) {
                this.xhr = xhr;
            },
            /**
             * @param {File} file
             */
            validateExtension(file) {
                if (!file) {
                    return false;
                }
                // allow everything if extensions is set to nothing
                if (!this.extensions || this.extensions.length <= 0) {
                    return true;
                }
                const ext = file.name.split(".").pop().toLowerCase(); // Get extension string from end of filename
                if (this.extensions.find((e) => ext === e)) {
                    return true;
                }
                return false;
            },
            /**
             * @param {Event} event
             */
            grabAndValidateFile(event) {
                const errorMessageBase = this.couldNotUploadMessage;
                let hasError = false;
                if (event.target.files == null || event.target.files.length <= 0) {
                    this.alertMessage = `${errorMessageBase} ${this.fileNotFoundMessage}`;
                    hasError = true;
                }
                const file = event.target.files[0];
                if (!hasError && file != null && file.name.length > MAX_ALLOWED_FILENAME_LENGTH) {
                    this.alertMessage = `${errorMessageBase} ${this.fileNameExccedsLimitText}`;
                    hasError = true;
                }
                if (!hasError && !this.validateExtension(file)) {
                    hasError = true;
                    if (this.extensions.length === 1) {
                        this.alertMessage = `${errorMessageBase} ${this.singleExtensionInvalidMessage}`;
                    } else {
                        this.alertMessage = `${errorMessageBase} ${this.multipleExtensionInvalidMessage} ${this.accept}`;
                    }
                }
                if (!hasError && file.size > this.maxAllowedSizeKb * 1024) {
                    hasError = true;
                    this.alertMessage = `${errorMessageBase} ${this.fileTooLargeMessage}`;
                }
                if (hasError) {
                    this.showAlert = true;
                    if (this.$refs.input) {
                        this.$refs.input.value = null;
                    }
                    return null;
                }
                return file;
            },
            keyupHandler(event) {
                if (event.key === "Enter" || event.key === " " || event.keyCode === 32 || event.keyCode === 13) {
                    this.$refs.input.click();
                }
            },
            dropHandler(event) {
                // Following the Bootstrap 5 changes, we put a label on an invisible
                // input element in order to exactly match the previous UI. The consequence
                // of that is we don't get the native input element behavior, so this is
                // to mimic that as best as possible.  Handle the drop event on the label,
                // manually change the input's .files to what the user dropped on the label,
                // then trigger the input's "change" event to process them. If user drops
                // multiple files, just ignore.
                event.preventDefault();
                if (event.dataTransfer?.items) {
                    const fileInput = this.$refs.input;
                    fileInput.files = event.dataTransfer.files;
                    fileInput.dispatchEvent(new Event("change"));
                }
            }
        }
    };
</script>

<style lang="scss" scoped>
    .pc-custom-file {
        position: relative;
        &::after {
            content: attr(data-browse);
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            z-index: 3;
            display: block;
            padding: 0.375rem 0.75rem;
            border-left: inherit;
            color: var(--bs-body-color);
            background-color: var(--bs-tertiary-bg);
        }
    }

    .pc-file-width-spacer {
        width: 1rem;
    }

    .bot-loading-bar {
        position: relative;
        overflow-x: hidden;
        &:after {
            position: absolute;
            content: "";
            width: 10%;
            bottom: 0;
            height: 3px;
            background-color: var(--bs-primary);
            animation: progress-slide 1.5s linear infinite;
        }
    }

    .progress-loop {
        filter: none;
        width: 50px;
        height: 100%;
        position: relative;
        background-color: var(--bs-primary);
        animation: progress-slide 1.5s linear infinite;
    }

    @keyframes progress-slide {
        0% {
            left: -50px;
        }
        50% {
            width: 50%;
        }
        100% {
            left: 100%;
        }
    }
</style>
